import React, { ReactNode, useEffect, useRef, useState } from 'react';
import useOnClickOutside from '../../../hooks/useOnClickOutside';
import DownChevronIcon from '../../../../assets/images/svg/DownChevronIcon';
import { classNames } from '../../../utils';
import { CustomClassContainer, CustomClassContainerProps } from '../../utility';

export type SelectOptionType<T> = {
  label: string;
  value: T;
};

interface SelectInputProps<T> extends CustomClassContainerProps {
  label: string;
  name: string;
  keyValue?: string;
  disabled?: boolean;
  options: SelectOptionType<T>[];
  placeholder?: string;
  value?: T;
  deselectable?: boolean;
  onChange: (value: T | undefined) => void;
  children?: ReactNode;
}

function SelectInput<T>({
  label,
  name,
  options,
  disabled = false,
  // keyValue = 'id',
  placeholder,
  onChange,
  value,
  classes,
  deselectable = true,
  children,
}: SelectInputProps<T>): JSX.Element {
  const selRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [currentSelected, setCurrentSelected] = useState<
    SelectOptionType<T> | undefined
  >(() => value && options.find((item) => item.value === value));

  const handleOnClickOutside = () => {
    setIsOpen(false);
  };

  useOnClickOutside(selRef, handleOnClickOutside);

  const toggleSelect = () => {
    if (disabled) {
      if (isOpen) {
        setIsOpen(false);
      }
      return;
    }
    setIsOpen(!isOpen);
  };

  const selectItem = (item: SelectOptionType<T>) => {
    if (
      currentSelected &&
      item.value === currentSelected.value &&
      !deselectable
    ) {
      toggleSelect();
      return;
    }

    if (currentSelected && item.value === currentSelected.value) {
      setCurrentSelected(undefined);
      onChange(undefined);
    } else {
      setCurrentSelected(item);
      onChange(item.value);
    }

    toggleSelect();
  };

  useEffect(() => {
    if (value && currentSelected?.value !== value) {
      const opt = options.find((item) => item.value === value);
      setCurrentSelected(opt);
    }
  }, [currentSelected?.value, options, value]);

  return (
    <CustomClassContainer classes={classes}>
      <div ref={selRef}>
        <div className='mb-2 flex flex-row items-center justify-between'>
          <label
            className='font-medium text-gray-dark text-base tracking-tight'
            htmlFor={name}
          >
            {label}
          </label>
          {children ? <div>{children}</div> : null}
        </div>
        <div>
          <button
            type='button'
            onClick={toggleSelect}
            className={classNames(
              'w-full flex flex-row items-center justify-between text-left rounded-lg border font-medium text-base shadow-sm px-5 py-4 appearance-none',
              disabled
                ? 'border-gray-lightest cursor-default'
                : 'border-gray-light focus:outline-none focus:ring-2 focus:ring-green',
            )}
          >
            {currentSelected && currentSelected.label ? (
              <span
                className={classNames(
                  'block flex-1 font-medium text-base',
                  disabled && 'text-gray-light',
                )}
              >
                {currentSelected.label}
              </span>
            ) : (
              <span className='block flex-1 font-medium text-base text-gray-light'>
                {placeholder ? placeholder : ''}
              </span>
            )}
            <div
              className={classNames(
                'bg-transparent transition ease-in duration-75 transform',
                isOpen ? 'rotate-180' : 'rotate-0',
                disabled ? 'text-gray-light' : 'text-gray-dark',
              )}
            >
              <DownChevronIcon width='27px' height='27px' strokeWidth='3' />
            </div>
          </button>
          <input
            id={name}
            className='hidden'
            defaultValue={value}
            name={name}
          />
        </div>
        {isOpen ? (
          <ul className='w-full border divide-y divide-gray-light border-gray-light shadow-sm mt-5 rounded-lg overflow-hidden'>
            {options.map((item, idx) => (
              <li key={`${item.label}__${idx}`}>
                <button
                  type='button'
                  className={`text-left w-full py-5 px-4 outline-none text-base font-medium text-gray-dark focus:outline-none ${
                    currentSelected?.label === item.label
                      ? 'bg-green-lightest'
                      : ''
                  }`}
                  onClick={() => selectItem(item)}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </CustomClassContainer>
  );
}

export default SelectInput;
